import { forwardRef, useEffect, useState } from "react";
import { Box, Grid2, Modal } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { IZoneSchedule, IZoneUserNumber } from "../../../../Domain/Types/FloorPlan/ZoneSchedule";
import { InfoSideBar } from "../InfoSidebar";
import { InfoBox } from "../InfoBox";
import { CompactTextField } from "../../../../../Common/CompactTextfield/CompactTextfield";
import { AssignBox } from "../AssignBox/AssignBox";
import { IFloorPayload } from "../../../../Domain/Types/FloorPlan/FloorPayload.type";
import { MultiInfoHeader } from "../MultiInfoHeader/MultiInfoHeader";
import { MiniTextInput } from "../../../../../Common/MiniTextInput/MiniTextInput";
import { InfoIconWithTooltip } from "../../../../../Title/InfoIcon";
import { handleUserNumbersInput, initialUserNumbers } from "./ZoneInfoSidebar.partial";
import {
  AssignedRoomlayout,
  RemoteRoomlayout,
  RoomlayoutIds
} from "../../../../Domain/Types/FloorPlan/RoomlayoutAssignment.type";
import { IEquipmentInventory } from "../../../../Domain/Types/FloorPlan/Equipment.type";
import { useEquipmentManagementActions } from "../../../../Hooks/useEquipmentManagement";
import { BookableEquipmentDialogContent } from "./BookableEquipmentDialogContent/BookableEquipmentDialogContent";
import { initMoreItemName } from "../functions/initMoreItemName.function";
import { findZoneBookingType } from "../../../Views/ReportFloorPlanView/ReportFloorPlanView.functions";
import { RoomLayoutDialogContent } from "./RoomLayoutDialogContent/RoomLayoutDialogContent.component";
import {
  initialBookableEquipmentAssign,
  initialCateringItemsAssign,
  initialRoomLayoutAssign
} from "./functions/ZoneServiceInfoSidebar.function";
import {
  AssignedBookableEquipment,
  BookableEquipmentIds
} from "../../../../Domain/Types/FloorPlan/BookableEquipmentAssignment.type";
import { RemoteCateringMenu } from "../../../../../ServiceManagement/Catering/Domain/Types/Remote-CateringMenu.types";
import {
  AssignedCateringMenu,
  CateringMenuIds
} from "../../../../Domain/Types/FloorPlan/CateringMenuAssignment.type";
import { CateringMenuDialogContent } from "./CateringMenuDialogContent/CateringMenuDialogContent";
import { useRemoteFetchBookableEquipments } from "../../../../../../hooks/Remote/BookableEquipment/useRemoteFetchBookableEquipment";
import { useRemoteFetchAllCateringServices } from "../../../../../../hooks/Remote/Services/CateringMenu/useRemoteFetchAllCateringServices";
import { useRemoteFetchRoomlayout } from "../../../../../../hooks/Remote/Services/Roomlayout/useRemoteFetchRoomlayout";

interface Props {
  height: number;
  zoneSchedules: IZoneSchedule[];
  floorPlan: IFloorPayload;
  locationInventoryId: number;
  equipmentManagement: useEquipmentManagementActions;
  onClose: () => void;
  onChangeUserNumber: (ids: number[], userNumber: IZoneUserNumber) => void;
  onChangeConfService: (
    id: number,
    service: {
      bookableEquipments?: BookableEquipmentIds[];
      cateringServices?: CateringMenuIds[];
      roomlayouts?: RoomlayoutIds[];
    }
  ) => void;
}

/** simple component for editing zone service info */
export const ZoneServiceInfoSidebar = forwardRef<HTMLDivElement, Props>(
  (
    {
      height,
      zoneSchedules,
      floorPlan,
      locationInventoryId,
      onClose,
      onChangeUserNumber,
      onChangeConfService
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const { minUsers, maxUsers } = initialUserNumbers(zoneSchedules);

    const zoneName = zoneSchedules[0]?.inventory?.name ?? "";
    const zoneDesc = zoneSchedules[0]?.description ?? "";

    const [zoneUserNumber, setZoneUserNumber] = useState<IZoneUserNumber>({
      minUsers: minUsers,
      maxUsers: maxUsers
    });

    /**
     * room layout
     */
    const { data: remoteRoomLayout } = useRemoteFetchRoomlayout();
    const [isRoomLayoutModalOpen, setIsRoomLayoutModalOpen] = useState(false);
    const [roomLayout, setRoomLayout] = useState<RemoteRoomlayout[]>([]);
    const [assignedRoomLayout, setAssignedRoomLayout] = useState<AssignedRoomlayout[]>([]);

    /**
     * bookable equipment
     */
    const { data: remoteBookableEquipment } = useRemoteFetchBookableEquipments();
    const [isBookableEquipmenteModalOpen, setIsBookableEquipmenteModalOpen] = useState(false);
    const [bookableEquipment, setBookableEquipment] = useState<IEquipmentInventory[]>([]);
    const [assignedBookableEquipment, setAssignedBookableEquipment] = useState<
      AssignedBookableEquipment[]
    >([]);

    /**
     * catering menu
     */
    const { data: remoteAllCateringServices } =
      useRemoteFetchAllCateringServices(locationInventoryId);
    const [isCateringModalOpen, setIsCateringModalOpen] = useState(false);
    const [allCateringServices, setAllCateringServices] = useState<RemoteCateringMenu[]>([]);
    const [assignedCateringItems, setAssignedCateringItems] = useState<AssignedCateringMenu[]>([]);

    useEffect(() => {
      // find the bookable equipments assigned to the selected zone and store the remote data
      initialRoomLayoutAssign({
        floorPlan,
        zoneSchedules,
        remoteRoomLayout,
        setRoomLayout,
        setAssignedRoomLayout
      });
    }, [floorPlan, zoneSchedules, remoteRoomLayout]);

    useEffect(() => {
      // find the bookable equipments assigned to the selected zone and store the remote data
      initialBookableEquipmentAssign({
        floorPlan,
        zoneSchedules,
        remoteBookableEquipment,
        setBookableEquipment,
        setAssignedBookableEquipment
      });
    }, [floorPlan, zoneSchedules, remoteBookableEquipment]);

    useEffect(() => {
      // find the catering items assigned to the selected zone and store the remote data
      initialCateringItemsAssign({
        floorPlan,
        zoneSchedules,
        remoteAllCateringServices,
        setAllCateringServices,
        setAssignedCateringItems
      });
    }, [floorPlan, zoneSchedules, remoteAllCateringServices]);

    useEffect(() => {
      setZoneUserNumber({
        minUsers: minUsers,
        maxUsers: maxUsers
      });
    }, [minUsers, maxUsers]);

    const zoneTypeId =
      zoneSchedules[0]?.zoneTypeId ||
      zoneSchedules[0]?.inventory?.zoneTypeId ||
      zoneSchedules[0]?.inventory?.zoneType?.id;

    const zoneTypeName = findZoneBookingType(zoneTypeId ?? 0);

    // length of selected zone
    const singleZoneSelected = zoneSchedules.length === 1;

    // check if multi selected zone have same type
    const isIdenticalZoneTypeSelected = zoneSchedules
      .map(z => z.zoneTypeId || z.inventory?.zoneTypeId)
      .every(v => v === (zoneSchedules[0].inventory?.zoneTypeId || zoneSchedules[0].zoneTypeId));

    // check if multi selected zone type includes conference zone
    const isConferenceOnlySelected = zoneSchedules
      .map(z => z.zoneTypeId || z.inventory?.zoneTypeId)
      .every(v => v === 3);

    return (
      <InfoSideBar height={height} width={290} overflow="scroll" ref={ref}>
        {zoneSchedules.length > 0 && (
          <InfoBox color={"#00000088"}>
            {/* header of multi zones edit and listing the names */}
            <MultiInfoHeader
              showMultiInfoHeader={zoneSchedules.length > 1}
              type={"Zone"}
              onClose={onClose}
            />

            {/* name of the zone */}
            <CompactTextField
              placeholder={t("Name")}
              value={zoneName}
              newText={zoneName}
              rows={2}
              readOnly={true}
            />

            {/* description of the zone */}
            <CompactTextField
              rows={2}
              placeholder={t("Description")}
              value={zoneDesc}
              newText={zoneDesc}
              readOnly={true}
            />

            {/* type of the zone */}
            {isIdenticalZoneTypeSelected && (
              <AssignBox
                buttonDataTestId="type-assign-box"
                value={
                  zoneSchedules[0].zoneTypeId ||
                  zoneSchedules[0].inventory?.zoneType?.id ||
                  zoneSchedules[0].inventory?.zoneTypeId
                }
                name={zoneTypeName}
                type={"Type"}
              />
            )}

            {/** inventory */}
            {singleZoneSelected && (
              <AssignBox
                value={zoneSchedules[0].inventory?.id}
                type={"Inventory"}
                name={zoneSchedules[0].inventory?.name}
              />
            )}

            {/** category */}
            <AssignBox
              type={"Category"}
              buttonDataTestId="assign-category-btn"
              value={zoneSchedules[0].category?.id}
              name={zoneSchedules[0].category?.name}
            />

            {/** property for conference zone */}
            {isConferenceOnlySelected && singleZoneSelected && (
              <>
                {/** min and max users number */}
                <Grid2
                  sx={{ py: "0.3rem", flexWrap: "nowrap" }}
                  data-testid="zone-userNumber-input"
                  container
                >
                  <Grid2>
                    <MiniTextInput
                      fields={[
                        {
                          id: "min",
                          type: "number",
                          placeholder: "Min",
                          value: zoneUserNumber.minUsers
                        },
                        {
                          id: "max",
                          type: "number",
                          placeholder: "Max",
                          value: zoneUserNumber.maxUsers
                        }
                      ]}
                      readOnly={true}
                      onInput={(id, value) => {
                        handleUserNumbersInput(
                          id,
                          value,
                          zoneSchedules[0].id,
                          zoneUserNumber,
                          setZoneUserNumber,
                          onChangeUserNumber,
                          enqueueSnackbar
                        );
                      }}
                    />
                  </Grid2>
                  <Grid2 data-testid="user-number-info-desc" sx={{ pt: "2px", pl: "2px" }}>
                    <InfoIconWithTooltip tooltipText={t("EditInfoUserNumberForConferenceZone")} />
                  </Grid2>
                </Grid2>

                {/* bookable equipment */}
                <AssignBox
                  buttonDataTestId="bookable-equipment-assign-box"
                  onClickAssign={() =>
                    setIsBookableEquipmenteModalOpen(!isBookableEquipmenteModalOpen)
                  }
                  type={"Bookable_Equipment"}
                  value={
                    assignedBookableEquipment.length
                      ? t(`${assignedBookableEquipment.length} (pcs)`)
                      : t("NoBookableEquipmentSidebarLabel")
                  }
                  valueArr={assignedBookableEquipment.map(eq => eq.name)}
                  name={initMoreItemName(assignedBookableEquipment, "Bookable_Equipment", true, t)}
                  isEditable
                  isMandatoryForService={assignedBookableEquipment.some(
                    eq => eq.selectingIsMandatory === true
                  )}
                />

                {/* catering menus */}
                <AssignBox
                  buttonDataTestId="catering-assign-box"
                  onClickAssign={() => setIsCateringModalOpen(!isCateringModalOpen)}
                  type={"Catering"}
                  value={
                    assignedCateringItems.length
                      ? t(`${assignedCateringItems.length} (pcs)`)
                      : t("NoCateringSidebarLabel")
                  }
                  valueArr={assignedCateringItems.map(eq => eq.name)}
                  name={initMoreItemName(assignedCateringItems, "Catering", true, t)}
                  isEditable
                  isMandatoryForService={assignedCateringItems.some(
                    ca => ca.selectingIsMandatory === true
                  )}
                />

                {/** room layout */}
                <AssignBox
                  onClickAssign={() => setIsRoomLayoutModalOpen(true)}
                  type={"RoomLayout"}
                  buttonDataTestId="assign-roomLayout-btn"
                  value={
                    assignedRoomLayout.length
                      ? t(`${assignedRoomLayout.length} (pcs)`)
                      : t("NoRoomLayoutSidebarLabel")
                  }
                  name={initMoreItemName(assignedRoomLayout, "RoomLayout", true, t)}
                  isEditable
                  isMandatoryForService={assignedRoomLayout.some(
                    ro => ro.selectingIsMandatory === true
                  )}
                />
              </>
            )}

            {/** bottom bar */}
            <Box display={"flex"} alignItems={"center"}>
              <Box flex={1} />
              {/* no items for zone service info */}
            </Box>

            {/* zone bookable equipment assignment modal */}
            <Modal
              open={isBookableEquipmenteModalOpen}
              onClose={() => setIsBookableEquipmenteModalOpen(false)}
              sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
              <Box>
                <BookableEquipmentDialogContent
                  zoneSchedule={zoneSchedules[0]}
                  bookableEquipment={bookableEquipment}
                  assignedBookableEquipment={assignedBookableEquipment}
                  onChangeConfService={onChangeConfService}
                  onClose={() => setIsBookableEquipmenteModalOpen(false)}
                />
              </Box>
            </Modal>

            {/* zone catering menu assignment modal */}
            <Modal
              open={isCateringModalOpen}
              onClose={() => setIsCateringModalOpen(false)}
              sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
              <Box>
                <CateringMenuDialogContent
                  zoneSchedule={zoneSchedules[0]}
                  allCateringServices={allCateringServices}
                  assignedCateringItems={assignedCateringItems}
                  onChangeConfService={onChangeConfService}
                  onClose={() => setIsCateringModalOpen(false)}
                />
              </Box>
            </Modal>

            {/* conference zone - room layout modal */}
            <Modal
              open={isRoomLayoutModalOpen}
              onClose={() => setIsRoomLayoutModalOpen(false)}
              sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
              <Box>
                <RoomLayoutDialogContent
                  roomLayout={roomLayout}
                  assignedRoomLayout={assignedRoomLayout}
                  zoneSchedule={zoneSchedules[0]}
                  onChangeConfService={onChangeConfService}
                  onClose={() => setIsRoomLayoutModalOpen(false)}
                />
              </Box>
            </Modal>
          </InfoBox>
        )}
      </InfoSideBar>
    );
  }
);
